import React, { useMemo } from 'react';
import SubPage from '#/common/components/SubPage';
import Assets from '../components/Assets';
import { Tab } from '#/common/components/SubPage/SubPageProps';
import If from '#/common/components/If';
import { Navigate, useSearchParams } from 'react-router-dom';
import type { Filter } from '#/common/components/DataGrid';
import useAssetsPropsCounts from '../hooks/useAssetPropsCountsQuery';
import Manufacturers from '../manufacturer/components/Manufacturers';
import AssetTypes from '../assetType/components/AssetTypes';
import Locations from '../location/components/Locations';
import Models from '../model/components/Models';
import useSession from '#/common/hooks/useSession';
import { UserRole } from '#/common/types/userTypes';
import PageActions from '#/common/components/SubPage/PageActions/PageActions';
import { PageAction } from '#/app/providers/PageProvider';

export type AssetsTab = 'list' | 'types' | 'manufacturers' | 'models' | 'locations';

export const defaultTab: AssetsTab = 'list';

export interface AssetsProps {
  readonly tab?: AssetsTab;
}

const tabs = [
  {
    key: 'list',
    text: 'Assets',
    extra: {
      newPath: '/assets/new',
      newLabel: 'Asset',
    },
  },
  {
    key: 'types',
    text: 'Types',
    extra: {
      newPath: '/assets/types/new',
      newLabel: 'Type',
    },
  },
  {
    key: 'manufacturers',
    text: 'Manufacturers',
    extra: {
      newPath: '/assets/manufacturers/new/details',
      newLabel: 'Manufacturer',
    },
  },
  {
    key: 'models',
    text: 'Models',
    extra: {
      newPath: '/assets/models/new/details',
      newLabel: 'Model',
    },
  },
  {
    key: 'locations',
    text: 'Locations',
    extra: {
      newPath: '/assets/locations/new/details',
      newLabel: 'Location',
    },
  },
] satisfies Tab<AssetsTab>[];

const canCreateRole: Record<UserRole, boolean> = {
  [UserRole.asset_manager]: true,
  [UserRole.trane_admin]: true,
  [UserRole.technician]: false,
};

const AssetsPage: React.FC<AssetsProps> = ({ tab }) => {
  const selectedTab = tabs.find((t) => t.key === tab);
  const title = tab === 'list' ? selectedTab?.text : `Asset ${selectedTab?.text}`;
  const { user } = useSession();
  const [searchParams] = useSearchParams();
  const tenantIdFromParams = searchParams.get('tenantId') || undefined;
  const assetPermanentFilters = useMemo<Filter[]>(() => {
    if (!tenantIdFromParams) return [];
    return [['tenantId', '=', tenantIdFromParams]];
  }, [tenantIdFromParams]);

  // Locations don't have an asset lifecycle "state" field - only Assets do. Reusing
  // `assetPermanentFilters` here (state <> retired) previously made every Locations grid
  // load in the Assets view crash, since `state` isn't a valid/joined field for the
  // Locations list query. Locations only need the tenant scoping, not the asset-state filter.
  const locationPermanentFilters = useMemo<Filter[]>(() => {
    if (!tenantIdFromParams) return [];
    return [['tenantId', '=', tenantIdFromParams]];
  }, [tenantIdFromParams]);

  const role = user?.role as UserRole;

  const { assets, types, manufacturers, models, locations } = useAssetsPropsCounts();

  const assetsTabs = useMemo(
    () =>
      tabs.map((t) => {
        if (t.key === 'list') return { ...t, count: assets };
        if (t.key === 'types') return { ...t, count: types };
        if (t.key === 'manufacturers') return { ...t, count: manufacturers };
        if (t.key === 'models') return { ...t, count: models };
        if (t.key === 'locations') return { ...t, count: locations };
        return t;
      }),
    [assets, types, manufacturers, models, locations],
  ) as Tab[];

  const invalidTab = tabs.every((t) => t.key !== tab);

  const canCreateNew = selectedTab && canCreateRole[role];
  const actions = useMemo(() => {
    const result: PageAction[] = [];
    if (canCreateNew) {
      result.push({
        id: 'new-asset-item',
        alwaysShow: true,
        type: 'button',
        props: {
          type: 'submit',
          icon: 'add',
          label: `Create New ${selectedTab.extra.newLabel}`,
          responsive: true,
          path: selectedTab.extra.newPath,
        },
      });
    }

    return result;
  }, [canCreateNew, selectedTab]);

  if (invalidTab) {
    const redirectTo = `../${defaultTab}`;
    return <Navigate to={redirectTo} replace />;
  }

  return (
    <SubPage
      title={title}
      tabs={assetsTabs}
      selectedTab={tab}
      type="list"
      hideSelectToggle={role === UserRole.technician}>
      <PageActions actions={actions} />
      <If condition={tab === 'list'}>
        <Assets permanentFilters={assetPermanentFilters} />
      </If>
      <If condition={tab === 'types'}>
        <AssetTypes />
      </If>
      <If condition={tab === 'manufacturers'}>
        <Manufacturers />
      </If>
      <If condition={tab === 'models'}>
        <Models />
      </If>
      <If condition={tab === 'locations'}>
        <Locations permanentFilters={locationPermanentFilters} />
      </If>
    </SubPage>
  );
};

export default React.memo(AssetsPage);
